import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DownloadProgressProps {
    fileName: string;
    isVisible: boolean;
    progress: number; // 0-100
    bytes: number;
    contentLength: number;
    isComplete: boolean;
    error?: string;
    onClose: () => void;
}

const DownloadProgress: React.FC<DownloadProgressProps> = ({
    fileName,
    isVisible,
    progress,
    bytes,
    contentLength,
    isComplete,
    error,
    onClose,
}) => {
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (isComplete && !error) {
            setShowSuccess(true);
            const timer = setTimeout(() => {
                setShowSuccess(false);
                onClose();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isComplete, error, onClose]);

    if (!isVisible) return null;

    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return createPortal(
        <div className="fixed inset-0 z-999999 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div
                className={`bg-white rounded-lg shadow-xl max-w-sm w-full p-6 ${error ? 'cursor-pointer' : ''}`}
                onClick={error ? onClose : undefined}
            >
                {/* Header */}
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Datei wird heruntergeladen</h2>

                {/* File name */}
                <p className="text-sm text-gray-600 mb-4 truncate" title={fileName}>
                    {fileName}
                </p>

                {/* Progress bar */}
                <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Progress text */}
                <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-600">
                        {formatBytes(bytes)} / {formatBytes(contentLength)}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                        {Math.round(progress)}%
                    </span>
                </div>

                {/* Status messages */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                        <p className="text-sm text-red-800">
                            Fehler beim Herunterladen: {error}
                        </p>
                        <p className="text-xs text-red-600 mt-2">
                            Tippen Sie, um zu schließen
                        </p>
                    </div>
                )}

                {showSuccess && isComplete && !error && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
                        <p className="text-sm text-green-800 flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Erfolgreich zur Galerie hinzugefügt
                        </p>
                    </div>
                )}

                {/* Loading spinner during download */}
                {!isComplete && !error && (
                    <>
                        <div className="flex justify-center mb-4">
                            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded p-3">
                            <p className="text-sm text-blue-800 text-center">
                                Bitte schließen Sie die App nicht während des Downloads
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default DownloadProgress;

